import { generateBasketKey } from "@scayle/storefront-core/dist/utils/keys";
import {
  BaseError,
  ExistingItemHandling,
  getShippingDates,
  HttpStatusCode,
  HttpStatusMessage
} from "@scayle/storefront-core";
import { useNuxtApp } from "nuxt/app";
import { computed } from "vue";
import { extendPromise } from "../../utils/promise.js";
import { useRpc } from "../core/useRpc.js";
import { useRpcCall } from "../core/useRpcCall.js";
import {
  toValue,
  toRef
} from "#imports";
export function useBasket({
  params,
  key = "useBasket"
} = {}) {
  const nuxtApp = useNuxtApp();
  const addItemToBasketRpc = useRpcCall("addItemToBasket");
  const addItemsToBasketRpc = useRpcCall("addItemsToBasket");
  const mergeBasketsRpc = useRpcCall("mergeBaskets");
  const clearBasketRpc = useRpcCall("clearBasket");
  const removeItemFromBasketRpc = useRpcCall("removeItemFromBasket");
  const asyncData = useRpc("getBasket", key, params, {
    server: false,
    // NOTE: In some cases it might be ok to fetch on server
    watch: [toRef(params)],
    dedupe: "defer",
    getCachedData: (key2) => {
      return toValue(nuxtApp._asyncData[key2]?.data) ?? void 0;
    }
  });
  const {
    data,
    pending,
    error,
    refresh,
    status
  } = asyncData;
  const addItem = async ({
    variantId,
    promotionId,
    quantity,
    existingItemHandling,
    displayData,
    customData,
    itemGroup
  }) => {
    data.value = await addItemToBasketRpc({
      promotionId,
      variantId,
      quantity,
      existingItemHandling,
      displayData,
      customData,
      itemGroup,
      with: toValue(params)
    });
  };
  const addItems = async (items2, existingItemHandling = ExistingItemHandling.AddQuantityToExisting) => {
    data.value = await addItemsToBasketRpc({
      items: items2,
      existingItemHandling,
      with: toValue(params)
    });
  };
  const mergeBaskets = async (args) => {
    return await mergeBasketsRpc({
      ...args,
      with: toValue(params)
    });
  };
  const findItem = (item) => {
    return data.value?.items?.find((entry) => {
      if ("variantId" in item) {
        return entry.variant.id === item.variantId;
      }
      return entry.product.id === item.productId;
    });
  };
  const contains = (item) => {
    return findItem(item) !== void 0;
  };
  const removeItem = async (item) => {
    const element = findItem(item);
    if (!element) {
      throw new BaseError(
        HttpStatusMessage.NOT_FOUND,
        HttpStatusCode.NOT_FOUND,
        `Could not find basket item by variant-id: ${item.variantId}`
      );
    }
    data.value = await removeItemFromBasketRpc({
      itemKey: element.key,
      with: toValue(params)
    });
  };
  const removeItemByKey = async (itemKey) => {
    data.value = await removeItemFromBasketRpc({
      itemKey,
      with: toValue(params)
    });
  };
  const clear = async () => {
    await clearBasketRpc();
  };
  const products = computed(
    () => data.value?.items.map((item) => item.product) || []
  );
  const count = computed(
    () => {
      return data.value?.items?.reduce((prev, current) => {
        if (current.itemGroup?.id && !current.itemGroup.isMainItem) {
          return prev;
        }
        return prev + current.quantity;
      }, 0);
    }
  );
  const countWithoutSoldOutItems = computed(
    () => {
      const mainItemQuantities = /* @__PURE__ */ new Map();
      const soldOutItemGroups = /* @__PURE__ */ new Set();
      let count2 = (data.value?.items ?? []).reduce((prev, current) => {
        if (current.itemGroup?.id) {
          if (current.itemGroup.isMainItem) {
            mainItemQuantities.set(current.itemGroup.id, current.quantity);
          } else {
            if (current.product.isSoldOut && current.itemGroup.isRequired) {
              soldOutItemGroups.add(current.itemGroup.id);
            }
            return prev;
          }
        }
        if (current.product.isSoldOut) {
          return prev;
        }
        return prev + current.quantity;
      }, 0);
      soldOutItemGroups.forEach((itemGroupId) => {
        count2 -= mainItemQuantities.get(itemGroupId) || 0;
      });
      return count2;
    }
  );
  const items = computed(() => data.value?.items);
  const cost = computed(() => data.value?.cost);
  const basketKey = computed(() => data.value?.key);
  const isEmpty = computed(() => count.value === 0);
  const packages = computed(() => data.value?.packages);
  const shippingDates = computed(
    () => packages.value ? getShippingDates(packages.value) : void 0
  );
  return extendPromise(asyncData, {
    data,
    items,
    cost,
    key: basketKey,
    packages,
    shippingDates,
    isEmpty,
    fetching: pending,
    pending,
    fetch: refresh,
    refresh,
    count,
    countWithoutSoldOutItems,
    addItem,
    addItems,
    removeItem,
    removeItemByKey,
    clear,
    contains,
    products,
    findItem,
    generateBasketKey,
    mergeBaskets,
    error,
    status
  });
}
